import React, { useRef, useCallback, useEffect, useMemo, useState } from "react";
import { Container, Sprite, Text } from "@pixi/react";
import * as PIXI from "pixi.js";
import { getNewUITexture } from "../../../../assets";
import { FONT_CONFIG } from "../../../../pixiSettings";
import { useButtonBrightness } from "../../../../Hooks/useButtonBrightness";

const BetButton = ({
  x,
  y,
  text,
  onClick,
  active = false,
  disabled = false,
  width,
  height,
  fontSize,
}) => {
  const containerRef = useRef(null);

  // Shared hover/press hook
  const { buttonRef, handlePointerOver, handlePointerOut, triggerPress, cleanupFilters } =
    useButtonBrightness({ brightness: 2.5 });


  const backgroundTexture = getNewUITexture("bet_buttons_background");
  const activeTexture = getNewUITexture("bet_buttons_background_active");
  const infinityTexture = getNewUITexture("icon_infinity");

  // Check if this is an infinity button
  const isInfinityButton = text === "∞";

  // Calculate infinity icon size maintaining aspect ratio
  const infinityIconSize = useMemo(() => {
    if (!isInfinityButton || !infinityTexture) return { width: 0, height: 0 };

    const iconAspectRatio = infinityTexture.width / infinityTexture.height;
    const maxWidth = width * 0.7;
    const maxHeight = height * 0.9;

    // Calculate size to fit within max dimensions while maintaining aspect ratio
    let iconWidth = maxWidth;
    let iconHeight = iconWidth / iconAspectRatio;

    // If height exceeds max, scale down by height instead
    if (iconHeight > maxHeight) {
      iconHeight = maxHeight;
      iconWidth = iconHeight * iconAspectRatio;
    }

    return { width: iconWidth, height: iconHeight };
  }, [isInfinityButton, infinityTexture, width, height]);

  /**
   * Auto-scaling text font size calculation
   *
   * Ensures text always fits within button width by:
   * 1. Using ideal font size (60% of button height)
   * 2. Measuring text width with ideal font size using PIXI.TextMetrics
   * 3. Comparing against max text width (90% of button width for padding)
   * 4. Only scaling down if text exceeds max width (proportional scaling)
   * 5. Returning ideal font size if text fits (preserves crisp rendering)
   *
   * This prevents text overflow while maintaining sharp text rendering for values
   * that fit naturally. Only long values (e.g., "1.000,00 FUN") get scaled down.
   */
  const finalFontSize = useMemo(() => {
    // Ideal font size - 60% of button height, rounded to even number for crisp rendering
    const idealFontSize = Math.round(height * 0.6);

    // Allow 90% of button width for text (10% padding on sides)
    const maxTextWidth = width * 0.85;

    // Measure text width with ideal font size
    const tempStyle = new PIXI.TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: idealFontSize,
      fontWeight: "400",
    });
    const metrics = PIXI.TextMetrics.measureText(text, tempStyle);

    // Only scale down if text exceeds max width
    if (metrics.width > maxTextWidth) {
      const scale = maxTextWidth / metrics.width;
      // Round to even number for crisp rendering
      return Math.round(idealFontSize * scale);
    }

    // Return ideal font size if it fits (preserves crisp rendering)
    return idealFontSize;
  }, [text, width, height]);

  // Text style for bet value
  const dpr = window.devicePixelRatio || 1;
  const textStyle = useMemo(
    () =>
      new PIXI.TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: finalFontSize,
        fontWeight: "400",
        fill: disabled ? 0x666666 : 0xffffff,
        align: "center",
        resolution: dpr,
      }),
    [finalFontSize, disabled, dpr]
  );

  // Round text position to whole pixels for crisp rendering
  const textX = Math.round(0);
  const textY = Math.round(0);

  // Track hover state to swap texture on hover
  const [isHovered, setIsHovered] = useState(false);

  // Hover: show yellow texture (swap in activeTexture)
  const handleBetPointerOver = useCallback(() => {
    if (!disabled) {
      setIsHovered(true);
      handlePointerOver();
    }
  }, [disabled, handlePointerOver]);

  // Pointer out: revert texture
  const handleBetPointerOut = useCallback(() => {
    setIsHovered(false);
    if (!active) {
      handlePointerOut();
    }
  }, [active, handlePointerOut]);

  const handleClick = useCallback(() => {
    if (disabled) return;

    // Add press animation
    if (buttonRef.current) {
      triggerPress();
    }

    onClick();
  }, [disabled, onClick, buttonRef, triggerPress]);

  // Apply brightness when active (highlight the selected bet)
  useEffect(() => {
    if (buttonRef.current && !disabled) {
      if (active) {
        handlePointerOver(); // reuse hook's brightness filter
      } else {
        handlePointerOut();
      }
    }
  }, [active, disabled, handlePointerOver, handlePointerOut, buttonRef]);

  // Clean up on unmount
  useEffect(() => () => cleanupFilters(), [cleanupFilters]);

  return (
    <Container ref={containerRef} name="secondary_bet_button" x={x} y={y}>
      <Sprite
        ref={buttonRef}
        texture={isHovered ? activeTexture : backgroundTexture}
        name="secondary_bet_button_background"
        anchor={0.5}
        width={width}
        height={height}
        roundPixels={true}
        alpha={disabled ? 0.5 : 1}
        eventMode={disabled ? "none" : "static"}
        cursor={disabled ? "default" : "pointer"}
        onpointertap={handleClick}
        onpointerover={handleBetPointerOver}
        onpointerout={handleBetPointerOut}
      />

      {isInfinityButton && infinityTexture ? (
        <Sprite
          texture={infinityTexture}
          anchor={0.5}
          width={infinityIconSize.width}
          height={infinityIconSize.height}
          roundPixels={true}
          x={textX}
          y={textY}
          alpha={disabled ? 0.5 : 1}
          eventMode="none"
        />
      ) : (
        <Text
          text={text}
          style={textStyle}
          anchor={0.5}
          x={textX}
          y={textY}
          roundPixels={true}
          eventMode="none"
        />
      )}
    </Container>
  );
};

export default BetButton;
